import React, { useState } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/cards/Card';
import { Button } from '../../components/buttons/Button';
import { StatCard } from '../../components/cards/StatCard';
import { PropertyForm } from '../../components/forms/PropertyForm';
import { Badge } from '../../components/common/Badge';
import { 
  LayoutDashboard, MessageSquare, Plus, BarChart3, 
  Users, Building, Trash2, CreditCard, DollarSign, 
  CheckCircle2, Clock, Calendar, Settings, AlertCircle,
  FileText, Search, ExternalLink, Zap, ChevronLeft
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';

export const LandlordDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { properties, deleteProperty } = useData();
  const { user } = useAuth();
  
  // Strict filter for landlord properties based on current user
  const myProperties = properties.filter(p => p.agentId === user?.id);
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);
  
  // Logic: Check if limit is reached (Max 3)
  const canAddProperty = myProperties.length < 3;

  const handleAddPropertyClick = () => {
    if (canAddProperty) {
      setActiveTab('add-property');
    } else {
      alert("You have reached the limit of 3 listings on the Early Access plan.");
    }
  };

  const sidebarItems = [
    { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'properties', label: 'My Properties', icon: Building },
    { id: 'add-property', label: 'Add Property', icon: Plus },
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
    { id: 'financials', label: 'Financials', icon: DollarSign },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // --- PENDING STATE ---
  if (user?.status === 'PENDING') {
    return (
      <DashboardLayout title="Landlord Dashboard" sidebarItems={sidebarItems.slice(0,1)} activeTab="overview" onTabChange={() => {}}>
        <div className="max-w-2xl mx-auto py-12">
          <Card className="p-8 text-center border-t-4 border-t-yellow-400 shadow-lg">
            <div className="w-20 h-20 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Under Review</h2>
            <p className="text-gray-600 mb-8 leading-relaxed max-w-lg mx-auto">
              We are currently verifying your landlord credentials. 
              This process ensures a safe environment for all students. 
              You will receive an email once approved (typically 24-48 hours).
            </p>
            <div className="bg-gray-50 rounded-xl p-6 text-left border border-gray-100">
              <h4 className="font-bold text-gray-900 mb-4">What happens next?</h4>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2 mt-0.5" /> Our team reviews your ID and property proof.</li>
                <li className="flex items-start"><CheckCircle2 className="w-4 h-4 text-gray-300 mr-2 mt-0.5" /> Once approved, you can publish listings.</li>
                <li className="flex items-start"><CheckCircle2 className="w-4 h-4 text-gray-300 mr-2 mt-0.5" /> You can list up to 3 properties for free.</li>
              </ul>
            </div>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // --- APPROVED DASHBOARD ---
  return (
    <DashboardLayout title="Landlord Portal" sidebarItems={sidebarItems} activeTab={activeTab} onTabChange={setActiveTab}>
      
      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
         <div className="space-y-8 animate-fadeIn">
            
            {/* Header / Account Status */}
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
               <div>
                  <h2 className="text-2xl font-bold text-gray-900">Welcome, {user?.name}</h2>
                  <div className="flex items-center gap-3 mt-2">
                     <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Account Verified
                     </span>
                     <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#2f5c96]/10 text-[#2f5c96]">
                        Early Access Plan
                     </span>
                  </div>
               </div>
               <div className="flex gap-3 w-full md:w-auto">
                  <Button 
                    onClick={handleAddPropertyClick} 
                    className={`w-full md:w-auto ${!canAddProperty ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={canAddProperty ? 'Add Property' : 'Limit of 3 listings reached'}
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Property {myProperties.length}/3
                  </Button>
               </div>
            </div>

            {/* Quick Stats (Calculated) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard label="Active Properties" value={`${myProperties.length} / 3`} icon={Building} />
              <StatCard label="Total Views" value="-" icon={BarChart3} trend="Tracking..." />
              <StatCard label="Available Units" value={myProperties.filter(p => p.status === 'AVAILABLE').length.toString()} icon={MessageSquare} color="blue" />
              <StatCard label="Occupancy" value={`${myProperties.length > 0 ? Math.round((myProperties.filter(p => p.status === 'RENTED').length / myProperties.length) * 100) : 0}%`} icon={Users} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
               
               {/* My Properties Preview (2/3) */}
               <div className="lg:col-span-2 space-y-6">
                  <div className="flex justify-between items-center">
                     <h3 className="text-lg font-bold text-gray-900">My Properties</h3>
                     <button onClick={() => setActiveTab('properties')} className="text-sm text-[#2f5c96] hover:underline">View All</button>
                  </div>
                  
                  {myProperties.length > 0 ? (
                     <div className="space-y-4">
                        {myProperties.slice(0, 3).map(p => (
                           <Card key={p.id} className="p-4 flex flex-col sm:flex-row gap-4 items-center group hover:border-[#2f5c96] transition-colors cursor-pointer">
                              <img src={p.image} className="w-full sm:w-24 h-24 rounded-lg object-cover" />
                              <div className="flex-1 w-full sm:w-auto">
                                 <div className="flex justify-between items-start">
                                    <h4 className="font-bold text-gray-900 group-hover:text-[#2f5c96]">{p.title}</h4>
                                    <Badge color={p.status === 'AVAILABLE' ? 'green' : 'gray'}>{p.status}</Badge>
                                 </div>
                                 <p className="text-sm text-gray-500 mb-1">{p.location}</p>
                                 <p className="text-sm font-bold text-[#2f5c96]">R{p.price}/mo</p>
                              </div>
                              <div className="flex gap-2 w-full sm:w-auto border-t sm:border-t-0 pt-4 sm:pt-0">
                                 <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={() => { setActiveTab('properties'); setEditingPropertyId(p.id); }}>Edit</Button>
                              </div>
                           </Card>
                        ))}
                     </div>
                  ) : (
                     <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                        <Building className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 mb-4">No properties listed yet.</p>
                        <Button size="sm" onClick={handleAddPropertyClick}>List First Property</Button>
                     </div>
                  )}
               </div>

               {/* Right Column: Messages & Alerts (1/3) */}
               <div className="space-y-8">
                  
                  {/* Messages Preview */}
                  <Card className="p-5">
                     <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-gray-900">Recent Activity</h3>
                        <button onClick={() => setActiveTab('messages')} className="text-xs text-[#2f5c96]">View All</button>
                     </div>
                     <div className="text-sm text-gray-500 text-center py-8">
                        No recent messages.
                     </div>
                  </Card>

               </div>
            </div>
         </div>
      )}
      
      {/* ADD PROPERTY TAB */}
      {activeTab === 'add-property' && (
        <div className="max-w-3xl mx-auto animate-fadeIn">
          {canAddProperty ? (
            <>
              <div className="mb-6 flex items-center gap-2">
                 <Button variant="ghost" onClick={() => setActiveTab('properties')} className="p-2 -ml-2"><Settings className="w-5 h-5 rotate-90" /></Button>
                 <h2 className="text-2xl font-bold text-gray-900">Add New Property</h2>
              </div>
              <div className="mb-4 p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800 flex items-center">
                 <Building className="w-4 h-4 mr-2" /> You are adding listing {myProperties.length + 1} of 3.
              </div>
              <PropertyForm onSuccess={() => setActiveTab('properties')} />
            </>
          ) : (
            <div className="text-center py-12">
               <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Building className="w-10 h-10 text-orange-500" />
               </div>
               <h2 className="text-2xl font-bold text-gray-900 mb-2">Listing Limit Reached</h2>
               <p className="text-gray-600 max-w-md mx-auto mb-6">
                  You have used all 3 free listings included in the Early Access plan.
               </p>
               <Button onClick={() => setActiveTab('properties')}>Manage Existing Listings</Button>
            </div>
          )}
        </div>
      )}

      {/* MY PROPERTIES TAB */}
      {activeTab === 'properties' && !editingPropertyId && (
         <div className="space-y-6 animate-fadeIn">
           <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                 <h2 className="text-2xl font-bold text-gray-900">My Portfolio</h2>
                 <p className="text-gray-500">Manage listings, edit details, and view status.</p>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                 <div className="relative flex-1 sm:w-64">
                    <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                    <input type="text" placeholder="Search properties..." className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#2f5c96] outline-none" />
                 </div>
                 <Button 
                    onClick={handleAddPropertyClick} 
                    disabled={!canAddProperty}
                    className={!canAddProperty ? 'opacity-50' : ''}
                 >
                    <Plus className="w-4 h-4 mr-2" /> Add
                 </Button>
              </div>
           </div>

           {myProperties.length > 0 ? (
             <div className="grid gap-4">
               {myProperties.map(p => (
                 <Card key={p.id} className="p-4 flex flex-col md:flex-row justify-between items-center gap-6 group hover:shadow-md transition-shadow">
                   <div className="flex gap-4 items-center w-full md:w-auto">
                      <img src={p.image} className="w-20 h-20 rounded-lg object-cover" />
                      <div>
                         <h3 className="font-bold text-gray-900">{p.title}</h3>
                         <p className="text-sm text-gray-500">{p.location}</p>
                         <div className="flex gap-2 mt-2">
                           <Badge color={p.status === 'AVAILABLE' ? 'green' : 'gray'}>{p.status}</Badge>
                           <span className="text-xs font-bold text-[#2f5c96] px-2 py-0.5 bg-blue-50 rounded">R{p.price}</span>
                         </div>
                      </div>
                   </div>
                   <div className="flex flex-row md:flex-row gap-3 w-full md:w-auto">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => window.location.href=`#/property/${p.id}`}>View</Button>
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditingPropertyId(p.id)}>Edit</Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50 flex-1" onClick={() => deleteProperty(p.id)}>
                         <Trash2 className="w-4 h-4" />
                      </Button>
                   </div>
                 </Card>
               ))}
             </div>
           ) : (
             <div className="text-center py-24 bg-white rounded-xl border border-dashed border-gray-300">
                <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Build your portfolio</h3>
                <p className="text-gray-500 mb-6">List your first property to start receiving inquiries.</p>
                <Button onClick={handleAddPropertyClick}>Add Property</Button>
             </div>
           )}
         </div>
      )}

      {/* EDIT LISTING SUB-VIEW */}
      {activeTab === 'properties' && editingPropertyId && (
         <div className="max-w-3xl mx-auto animate-fadeIn">
            <div className="mb-6 flex items-center gap-2">
               <Button variant="ghost" onClick={() => setEditingPropertyId(null)} className="p-2 -ml-2"><ChevronLeft className="w-5 h-5" /> Back</Button>
               <h2 className="text-2xl font-bold text-gray-900">Edit Listing</h2>
            </div>
            <PropertyForm 
               initialData={properties.find(p => p.id === editingPropertyId)}
               onSuccess={() => setEditingPropertyId(null)} 
            />
         </div>
      )}

      {/* SUBSCRIPTION TAB */}
      {activeTab === 'subscription' && (
         <div className="max-w-4xl mx-auto animate-fadeIn space-y-8">
            <h2 className="text-2xl font-bold text-gray-900">Subscription & Billing</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
               {/* Current Plan Card */}
               <Card className="p-8 border-t-4 border-t-[#2f5c96] shadow-lg relative overflow-hidden">
                  <div className="flex justify-between items-start mb-6">
                     <div>
                        <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">Current Plan</p>
                        <h3 className="text-3xl font-extrabold text-gray-900">Early Access</h3>
                     </div>
                     <Badge color="green">Active</Badge>
                  </div>
                  
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                     <p className="text-sm text-blue-800 font-medium mb-1">Listings Used</p>
                     <div className="flex justify-between items-end mb-1">
                        <span className="text-2xl font-bold text-[#2f5c96]">{myProperties.length}</span>
                        <span className="text-sm text-gray-500">of 3 limit</span>
                     </div>
                     <div className="w-full bg-blue-200 rounded-full h-2">
                        <div className="bg-[#2f5c96] h-2 rounded-full transition-all duration-500" style={{width: `${(myProperties.length / 3) * 100}%`}}></div>
                     </div>
                  </div>

                  <div className="space-y-4 mb-8">
                     <p className="text-gray-600 flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2"/> Max 3 Active Listings</p>
                     <p className="text-gray-600 flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2"/> Tenant Screening Included</p>
                     <p className="text-gray-600 flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2"/> Performance Analytics</p>
                     <p className="text-gray-600 flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2"/> Priority Support</p>
                  </div>
                  
                  <div className="pt-6 border-t border-gray-100 text-center">
                     <p className="text-sm text-gray-500">Enjoy full access for free during our beta period.</p>
                  </div>
               </Card>

               {/* Payment Method Card - Simplified for Free Tier */}
               <Card className="p-8 flex flex-col justify-center items-center text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-400">
                     <Zap className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">No Payment Needed</h3>
                  <p className="text-gray-500 mb-6">
                     Since you are on the Early Access plan, no payment method is required at this time.
                  </p>
                  <Button variant="outline" size="sm" disabled className="opacity-50 cursor-not-allowed w-full">Manage Billing</Button>
               </Card>
            </div>
         </div>
      )}

      {/* FINANCIALS TAB (Placeholder) */}
      {activeTab === 'financials' && (
         <div className="space-y-6 animate-fadeIn">
            <h2 className="text-2xl font-bold text-gray-900">Financial Performance</h2>
            <Card className="p-12 text-center border-dashed bg-slate-50">
               <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
               <h3 className="text-lg font-bold text-gray-900">Financial Reports Coming Soon</h3>
               <p className="text-gray-500 max-w-md mx-auto">
                  Detailed revenue tracking, expense management, and tax reports will be available in the next update.
               </p>
            </Card>
         </div>
      )}

      {/* MESSAGES TAB */}
      {activeTab === 'messages' && (
         <div className="h-[calc(100vh-200px)] flex flex-col animate-fadeIn">
            <div className="flex justify-between items-center mb-6">
               <h2 className="text-2xl font-bold text-gray-900">Tenant Messages</h2>
               <div className="flex gap-2">
                  <Button variant="outline" size="sm">Unread</Button>
               </div>
            </div>
            
            <Card className="flex-1 flex overflow-hidden">
               {/* Inbox List */}
               <div className="w-full md:w-1/3 border-r border-gray-100 flex flex-col">
                  <div className="p-4 border-b border-gray-100">
                     <div className="relative">
                        <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                        <input type="text" placeholder="Search..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#2f5c96]" />
                     </div>
                  </div>
                  <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                     No messages found.
                  </div>
               </div>
               
               {/* Empty State for Chat */}
               <div className="hidden md:flex flex-1 flex-col items-center justify-center bg-slate-50">
                  <MessageSquare className="w-16 h-16 text-gray-300 mb-4" />
                  <p className="text-gray-500">Select a conversation to view messages</p>
               </div>
            </Card>
         </div>
      )}

      {/* SETTINGS TAB */}
      {activeTab === 'settings' && (
         <div className="max-w-2xl animate-fadeIn">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Account Settings</h2>
            <Card className="p-8">
               <form className="space-y-6">
                  <div>
                     <h3 className="text-lg font-bold text-gray-900 mb-4">Landlord Profile</h3>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                           <input type="text" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={user?.name} />
                        </div>
                        <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">Company (Optional)</label>
                           <input type="text" className="w-full p-2 border border-gray-300 rounded-md" placeholder="e.g. Smith Holdings" />
                        </div>
                        <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                           <input type="email" className="w-full p-2 border border-gray-300 rounded-md bg-gray-50" defaultValue={user?.email} readOnly />
                        </div>
                        <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                           <input type="tel" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={user?.phoneNumber} />
                        </div>
                     </div>
                  </div>

                  <div className="pt-4 border-t border-gray-100">
                     <h3 className="text-lg font-bold text-gray-900 mb-4">Preferences</h3>
                     <div className="space-y-2">
                        <label className="flex items-center gap-2">
                           <input type="checkbox" defaultChecked className="text-[#2f5c96] rounded focus:ring-[#2f5c96]" />
                           <span className="text-sm text-gray-600">Receive inquiry notifications via Email</span>
                        </label>
                        <label className="flex items-center gap-2">
                           <input type="checkbox" defaultChecked className="text-[#2f5c96] rounded focus:ring-[#2f5c96]" />
                           <span className="text-sm text-gray-600">Receive weekly performance reports</span>
                        </label>
                     </div>
                  </div>

                  <div className="pt-6">
                     <Button>Save Changes</Button>
                  </div>
               </form>
            </Card>
         </div>
      )}

    </DashboardLayout>
  );
};